import classnames from 'classnames'
import { css } from 'emotion'
import * as React from 'react'
import { connect } from 'react-redux'

import { createAccount } from 'common/actions'
import { loanEmailSelector } from 'embed/selectors/application'
import { MultiProductDispatch } from 'embed/constants'
import { Fields } from 'embed/constants/fields'
import { MultiProductState } from 'embed/reducers'

import { ApplicationFieldView as ApplicationField } from 'embed/components/ApplicationField'
import ThemeConsumer from 'embed/components/ThemeConsumer'
import { Button } from '../../../Button'
import { useCreateAccount } from '../../../Loan/CreateAccount/useCreateAccount'

const styles = require('./styles')
const commonStyles = require('embed/styles/application')

export interface DispatchProps {
  dispatchCreateAccount: (password: string) => Promise<string>
}

export interface StoreProps {
  email: string
}

type Props = StoreProps & DispatchProps

const UnconnectedCreateAccount: React.SFC<Props> = (props) => {
  const { dispatchCreateAccount, email } = props

  const {
    error,
    isLoading,
    isValid,
    onBlur,
    onChange,
    onContinue,
    onKeyDown,
    password,
    shouldShowValidationStatus,
  } = useCreateAccount(dispatchCreateAccount)

  const [showForm, setShowForm] = React.useState(false)
  const onPromptContinue = () => setShowForm(true)

  const prompt = (
    <>
      <h4>Create an account to save your results.</h4>
      <p>Plus you&rsquo;ll get recommended financial products and services personalized for you!</p>
      <Button className={styles.button} onClick={onPromptContinue}>
        Create Account
      </Button>
    </>
  )

  const form = (
    <ThemeConsumer>
      {({ errorStyles }) => (
        <>
          <h4>Create a password for {email}</h4>
          <ApplicationField
            styles={styles.field}
            field={Fields.Password}
            fieldData={{ isValid, shouldShowValidationStatus, value: password }}
            onBlur={onBlur}
            onChange={onChange}
            onKeyDown={onKeyDown}
            placeholder="Please choose a password"
          />
          {error && (
            <p className={styles.error} style={errorStyles}>
              {error}
            </p>
          )}
          <Button className={styles.button} disabled={!isValid} loading={isLoading} onClick={onContinue}>
            Submit
          </Button>
          <div className={classnames(commonStyles.formRow, commonStyles.consentField, styles.disclaimer)}>
            <label>
              <span>
                By entering a password and selecting ‘Submit’ I agree and accept Fiona's{' '}
                <a rel="noreferrer" target="_blank" href="https://fiona.com/terms">
                  Terms of Service
                </a>
                ,{' '}
                <a rel="noreferrer" target="_blank" href="https://fiona.com/privacy">
                  Privacy Policy
                </a>{' '}
                and{' '}
                <a
                  rel="noreferrer"
                  target="_blank"
                  href="https://fiona-directus-files-production.s3.amazonaws.com/directus/originals/Even+GLBA+Model+Form+Rev+8721+(3).pdf"
                >
                  GLBA Privacy Notice
                </a>
              </span>
            </label>
          </div>
        </>
      )}
    </ThemeConsumer>
  )

  return (
    <ThemeConsumer>
      {({ borderRadiusLarge, backdropStyles, colorBorder }) => (
        <div
          className={classnames(
            styles.container,
            css`
              background-color: ${backdropStyles.backgroundColor};
              border-color: ${colorBorder};
              border-radius: ${borderRadiusLarge};
            `,
          )}
        >
          {showForm ? form : prompt}
        </div>
      )}
    </ThemeConsumer>
  )
}

const mapStateToProps = (state: MultiProductState) => ({
  email: loanEmailSelector(state),
})

const mapDispatchToProps = (dispatch: MultiProductDispatch): DispatchProps => ({
  dispatchCreateAccount: (password: string) => dispatch(createAccount(password)),
})

export const CreateAccount = connect(mapStateToProps, mapDispatchToProps)(UnconnectedCreateAccount)
